import React, { useCallback, useEffect, useRef, useState } from 'react';
import enrichmentApi from '../services/enrichmentApi';
import Icon from './Icon';
import { timeAgo } from '../utils/time';
import './ResearchButton.css';

const POLL_MS = 3000;
const KIND = 'site_audit';

const summary = (before, findings, lastTask) => {
  const fresh = findings.filter(finding => !before.has(finding.id));
  const filled = fresh.filter(finding => finding.status === 'applied').length;
  const waiting = fresh.filter(finding => finding.status === 'proposed').length;
  if (!filled && !waiting) {
    if (lastTask?.status === 'failed') return `The audit couldn't run: ${lastTask.note || 'unknown error'}.`;
    return "The audit found nothing it could show a source for.";
  }
  return [filled ? `Filled in ${filled}` : null, waiting ? `${waiting} waiting for review` : null]
    .filter(Boolean).join(', ') + '.';
};

/** Crawls a company's own site for its offerings and who to reach out to. Takes 10-15 minutes. */
const SiteAuditButton = ({ subjectType, subjectId, onFinished }) => {
  const [active, setActive] = useState(false);
  const [message, setMessage] = useState(null);
  const [lastRun, setLastRun] = useState(null);
  const known = useRef(new Set());
  const timer = useRef(null);

  const loadLastRun = useCallback(() => {
    enrichmentApi.subject(subjectType, subjectId, KIND)
      .then(state => setLastRun(state.last_task))
      .catch(() => {
        // Non-critical: the button still works without a "Last run" line
      });
  }, [subjectType, subjectId]);

  const watch = useCallback(async () => {
    try {
      const state = await enrichmentApi.subject(subjectType, subjectId);
      if (state.active) {
        timer.current = setTimeout(watch, POLL_MS);
        return;
      }
      setActive(false);
      setMessage(summary(known.current, state.findings, state.last_task));
      loadLastRun();
      onFinished?.();
    } catch (err) {
      setActive(false);
      setMessage(err.message);
    }
  }, [subjectType, subjectId, onFinished, loadLastRun]);

  useEffect(() => {
    setActive(false);
    setMessage(null);
    setLastRun(null);
    loadLastRun();
    return () => clearTimeout(timer.current);
  }, [subjectType, subjectId, loadLastRun]);

  const start = async () => {
    setMessage(null);
    setActive(true);
    try {
      const state = await enrichmentApi.subject(subjectType, subjectId);
      known.current = new Set(state.findings.map(finding => finding.id));
      await enrichmentApi.startRun(subjectType, { ids: [subjectId], kind: 'site_audit' });
      timer.current = setTimeout(watch, POLL_MS);
    } catch (err) {
      setActive(false);
      setMessage(err.message);
    }
  };

  return (
    <div className="research-button">
      <button type="button" className="rm-button" onClick={start} disabled={active}>
        <Icon name={active ? 'loader' : 'file-text'} spin={active} />
        {active ? 'Auditing site… (usually 10–15 minutes)' : 'Audit site'}
      </button>
      {message
        ? <p role="status">{message}</p>
        : lastRun?.finished_at && <p className="research-button__last-run">Last run: {timeAgo(lastRun.finished_at)}</p>}
    </div>
  );
};

export default SiteAuditButton;
